import {Account} from "../../accounts/types.ts";

/**
 * Determines if a user should authenticate via federated identity provider (SSO)
 * or native AWS Cognito username/password authentication.
 * 
 * @param account - Account object from API response
 * @returns true if user should use federated authentication, false for native Cognito
 */
export const hasIdProvider = (account: Account): boolean => {
    // Input validation
    if (!account || typeof account !== 'object') {
        return false;
    }

    // If no username, user doesn't exist - check tenant configuration
    if (!account.username) {
        const providers = account['federated-id-providers'] || account.federatedIdProviders;
        return (providers?.length ?? 0) > 0;
    }
    
    // Check for UUID pattern (native Cognito users)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(account.username)) {
        return false; // Native Cognito user
    }
    
    // Check for federated pattern: ProviderName_anything
    const underscoreIndex = account.username.indexOf('_');
    if (underscoreIndex > 0) {
        const providerPart = account.username.substring(0, underscoreIndex);
        
        // Validate provider part (basic sanitization)
        if (!providerPart || providerPart.length > 100) {
            return false;
        }
        
        // Try multiple property names for federated providers (API inconsistency)
        const federatedProviders = account['federated-id-providers'] || 
                                 account.federatedIdProviders || 
                                 [];
        
        // Validate provider exists in federated providers array
        const hasMatchingProvider = federatedProviders.some(
            provider => provider && provider.name.includes(providerPart)
        );
        
        return hasMatchingProvider;
    }
    
    return false; // Default to native if pattern doesn't match
};

/**
 * Gets the federated identity provider for an account
 * @param account - Account object
 * @returns The federated provider object or null
 */
export const getIdProvider = (account: Account) => {
    if (!hasIdProvider(account)) {
        return null;
    }

    // Extract provider name from username
    if (account.username) {
        const underscoreIndex = account.username.indexOf('_');
        if (underscoreIndex > 0) {
            const providerName = account.username.substring(0, underscoreIndex);
            
            // Find matching provider in the array
            const federatedProviders = account['federated-id-providers'] || account.federatedIdProviders || [];
            const provider = federatedProviders.find(p => p && p.name.includes(providerName));
            
            return provider || null;
        }
    }
    
    return null;
};

